import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FolderStructure, {
    FolderIcon,
} from '../../components/shared/folders/FolderStructure';
import './YourEngagements.scss';
import useToast from '../../helpers/useToast';
import useLocale from '../../locale/useLocale';
import {
    useGetV1Folder,
    useGetV1FolderFolderIdUsers,
    usePutV1FolderFolderId,
    usePostV1Folder,
    usePutV1FolderFolderIdArchive,
    useGetV1IdentityUserInfo,
    useGetV1ProcessGetProcessesInFolderFolderId,
    useGetV1FolderFolderIdGetFolderDetails,
} from '../../api/generated/endpoint';
import {
    EditFolderRequest,
    FolderResponse,
    ProcessResponse,
    PaginatedFolderUsersResponse,
    CreateFolderRequest,
} from '../../api/generated/models';
import { AppRoutes } from '../../Constants';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import FolderConfig from '../../components/pages/engagements/FolderConfig';
import Breadcrumbs from '../../components/shared/breadcrumbs/Breadcrumbs';
import { ListingTable } from '../../components/shared/listingTable';
import { TreeNode } from '@appkit4/react-components/tree';
import { Loading } from '@appkit4/react-components/loading';
import { Button } from '@appkit4/react-components/button';
import { Search } from '@appkit4/react-components/search';
import { Input } from '@appkit4/react-components/field';
import { DeleteModal } from '../../components/shared/deleteModal';
import Illustration4 from '../../assets/illustrations/Illustration_4.png';
import Illustration5 from '../../assets/illustrations/Illustration_5.png';
import ArchiveFolder from '../../assets/illustrations/archive_folder.png';
import { tabs, columns } from './YourEngagements.data';
import { formatDate } from '../../helpers/helperFunctions';
import { Tooltip } from '@appkit4/react-components/tooltip';

const MyProcesses: React.FC = () => {
    const { folderId: urlFolderId } = useParams<{ folderId: string }>();
    const [folders, setFolders] = useState<FolderResponse[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number>(-1);
    const [inputValue, setInputValue] = useState('');
    const [folderName, setFolderName] = useState<string>('');
    const [breadcrumbs, setBreadcrumbs] = useState<
        { label: string; href?: string }[]
    >([{ label: 'My Engagements', href: '/folder' }]);
    const [isEditingFolderName, setisEditingFolderName] =
        useState<boolean>(false);
    const [hasAccess, setHasAccess] = useState<boolean>(true);
    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
    const [confirmationVisible, setConfirmationVisible] = useState(false);
    const [users, setUsers] = useState<PaginatedFolderUsersResponse | null>(
        null
    );
    const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] =
        useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [processes, setProcesses] = useState<ProcessResponse[]>([]);
    const [activeTab, setActiveTab] = useState(0);

    const navigate = useNavigate();
    const showToast = useToast();
    const { l } = useLocale();

    const { data: folderDetails } =
        useGetV1FolderFolderIdGetFolderDetails(selectedFolderId);

    const {
        data: folderData,
        isLoading,
        refetch: refetchFolders,
    } = useGetV1Folder({
        parentFolderId: selectedFolderId === -1 ? undefined : selectedFolderId,
    });

    useEffect(() => {
        const newFolderId = urlFolderId ? parseInt(urlFolderId, 10) : -1;
        setSelectedFolderId(newFolderId);

        if (refetchFolders) {
            refetchFolders();
        }
    }, [urlFolderId, refetchFolders]);

    useEffect(() => {
        if (folderDetails && selectedFolderId !== -1) {
            setFolderName(folderDetails.folderName || '');

            const breadcrumbItems = [
                { label: 'My Engagements', href: '/folder' },
            ];

            if (folderDetails.parentHierarchy) {
                const addFolderToBreadcrumbs = (folder: any) => {
                    if (folder) {
                        breadcrumbItems.push({
                            label: folder.folderName || '',
                            href: `/folder/${folder.folderID}`,
                        });

                        if (folder.childFolder) {
                            addFolderToBreadcrumbs(folder.childFolder);
                        }
                    }
                };

                addFolderToBreadcrumbs(folderDetails.parentHierarchy);
            }

            if (
                breadcrumbItems.length > 1 &&
                breadcrumbItems[breadcrumbItems.length - 1].label ===
                    folderDetails.folderName
            ) {
                breadcrumbItems.pop();
            }

            breadcrumbItems.push({
                label: folderDetails.folderName || '',
                href: '',
            });

            setBreadcrumbs(breadcrumbItems);
        } else if (selectedFolderId === -1) {
            setBreadcrumbs([{ label: 'My Engagements', href: '/folder' }]);
        }
    }, [folderDetails, selectedFolderId]);

    const { data: initialFolders, refetch } = useGetV1Folder();
    const { data: processesData } = useGetV1ProcessGetProcessesInFolderFolderId(
        selectedFolderId ?? null
    );
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5;

    useEffect(() => {
        if (newFolderName === '') {
            refetch();
        }
    }, [newFolderName, refetch]);

    useEffect(() => {
        if (selectedFolderId !== -1) {
            let filtered = [...(processesData?.processes ?? [])];

            if (activeTab === 1) {
                filtered = filtered.filter(
                    (process) => process.processTypeId === 1
                );
            } else if (activeTab === 2) {
                filtered = filtered.filter(
                    (process) => process.processTypeId === 3
                );
            } else if (activeTab === 3) {
                filtered = filtered.filter(
                    (process) => process.processTypeId === 3
                );
            }
            setProcesses(filtered);
            setCurrentPage(1);
        }
    }, [selectedFolderId, processesData, activeTab]);

    useEffect(() => {
        if (initialFolders) {
            setFolders(initialFolders.folders ?? []);
        }
    }, [initialFolders]);

    const { data: folderUsers, refetch: refetchFolderUsers } =
        useGetV1FolderFolderIdUsers(selectedFolderId ?? -1);

    useEffect(() => {
        if (folderUsers) {
            setUsers(folderUsers);
        }
    }, [folderUsers]);

    const { data: userInfo } = useGetV1IdentityUserInfo();

    useEffect(() => {
        if (userInfo && users) {
            const currentUser = users.folderUsers.find(
                (user) => user.userId === userInfo.id
            );
            if (currentUser) {
                setHasAccess(currentUser.folderPersonaId !== 3);
            } else {
                setHasAccess(userInfo.roleId !== 1);
            }
        }
    }, [userInfo, users]);

    const handleSelectFolder = (folderName: string, folderId: number) => {
        setInputValue('');
        if (folderId === -1) {
            navigate('/folder');
            return;
        }
        navigate(`/folder/${folderId}`);
    };

    const handleBreadcrumbClick = (index: number) => {
        const activeBreadcrumb = breadcrumbs[index];
        if (activeBreadcrumb.href) {
            navigate(activeBreadcrumb.href);
        }
    };

    const handleArchive = () => setConfirmationVisible(true);

    const confirmDeletion = () => {
        const parentId = folderDetails?.parentHierarchy?.folderID;
        archiveFolder(
            { folderId: selectedFolderId },
            {
                onSuccess: () => {
                    showToast(l('_success'), l('_folderDeleted'), 'success');
                    setFolders(
                        folders.filter(
                            (folder) => folder.folderId !== selectedFolderId
                        )
                    );
                    if (
                        parentId === null ||
                        parentId === undefined ||
                        parentId === selectedFolderId
                    ) {
                        // If no parent (root level folder), go to root
                        navigate('/folder');
                        setSelectedFolderId(-1);
                    } else {
                        // Navigate to the specific parent folder
                        navigate(`/folder/${parentId}`);
                        setSelectedFolderId(parentId);
                    }
                    setInputValue('');
                    setConfirmationVisible(false);
                },
                onError: () => {
                    showToast('Error', 'Failed to delete folder', 'error');
                    setInputValue('');
                    setConfirmationVisible(false);
                },
            }
        );
    };

    const deleteModalContent = () => (
        <div className="del-modal-content-wrapper">
            <img
                src={ArchiveFolder}
                alt="Delete Confirmation"
                className="del-modal-image"
            />
            <p>Are you sure you want to delete this entire folder?</p>
            <Input
                type="text"
                placeholder="Delete"
                className="del-folder-confirmation-input"
                value={inputValue}
                onChange={(value: string) => setInputValue(value)}
            />
            <p style={{ fontSize: 14 }}>Type “Delete” To Delete This Folder</p>
            <Button
                onClick={confirmDeletion}
                kind="danger"
                disabled={inputValue.toLowerCase() !== 'delete'}
                style={{ marginTop: '40px' }}
            >
                Delete
            </Button>
        </div>
    );

    const createFolderModalContent = () => (
        <div className="create-folder-modal-content">
            <div className="modal-content">
                <p>Please enter a name for your new folder:</p>
                <Input
                    type="text"
                    placeholder="Folder Name"
                    value={newFolderName}
                    onChange={(value: string) => setNewFolderName(value)}
                    style={{ marginTop: '10px' }}
                />
                <div className="modal-buttons">
                    <Button onClick={() => setIsCreateFolderModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreateFolderConfirm}>Create</Button>
                </div>
            </div>
        </div>
    );

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFolderName(e.target.value);
    };

    const handleEditClick = () => {
        setisEditingFolderName(true);
    };

    const showGenericErrorToast = useGenericErrorToast();

    const handleFolderNameChange = (newName: string) => {
        setFolders((prevFolders) =>
            prevFolders.map((folder) =>
                folder.folderId === selectedFolderId
                    ? { ...folder, folderName: newName }
                    : folder
            )
        );
    };

    const { mutate: updateFolder } = usePutV1FolderFolderId();
    const { mutate: createFolder } = usePostV1Folder();
    const { mutate: archiveFolder } = usePutV1FolderFolderIdArchive();

    const handleCreateFolder = () => {
        setIsCreateFolderModalOpen(true);
    };

    const handleCreateFolderConfirm = () => {
        if (newFolderName) {
            const requestData: CreateFolderRequest = {
                folderName: newFolderName,
                parentFolderId:
                    selectedFolderId === -1 ? null : selectedFolderId,
            };
            const newFolder = { data: requestData };

            createFolder(newFolder, {
                onSuccess: () => {
                    showToast(
                        'Success',
                        'Folder created successfully',
                        'success'
                    );
                    setNewFolderName('');
                    refetchFolders();
                },
                onError: () => {
                    showToast('Error', 'Failed to create folder', 'error');
                },
            });
        }
        setIsCreateFolderModalOpen(false);
    };

    const handleSaveClick = () => {
        if (folderName.trim()) {
            const requestData: EditFolderRequest = { folderName };
            updateFolder(
                { folderId: selectedFolderId, data: requestData },
                {
                    onSuccess: () => {
                        setisEditingFolderName(false);
                        handleFolderNameChange(folderName);
                        showToast(
                            l('_success'),
                            l('_folderNameUpdated'),
                            'success'
                        );
                    },
                    onError: (error) => {
                        showGenericErrorToast(error);
                    },
                }
            );
        } else {
            showToast(l('_error'), l('_folderNameEmpty'), 'error');
        }
    };

    const handleCreateProcess = (processType: number) => {
        if (selectedFolderId !== -1) {
            const path = `${AppRoutes.CreateProcess}?folderId=${selectedFolderId}&processType=${processType}`;
            navigate(path, {
                state: {
                    folderId: selectedFolderId,
                    folderName,
                    processType,
                },
            });
        } else {
            showToast(l('_createProcess'), l('_folderNotSelected'), 'error');
        }
    };

    const transformProcessesData = (processes: Array<any>) => {
        return processes.map((process) => ({
            id: process.processId,
            engagementName: process.processName,
            documentType: process.documentTypeNames,
            owner: {
                name: process.createdByUserName,
                initials: getInitials(process.createdByUserName),
            },
            lastUpdated: formatDate(process.creationDateTime),
            engagementType: process.processTypeName,
            validationStatus: getValidationStatus(process), // derive status as needed
        }));
    };

    const getInitials = (name: string) => {
        const nameParts = name.split(' ');
        return nameParts
            .map((part) => part.charAt(0))
            .join('')
            .toUpperCase();
    };

    const getValidationStatus = (process: any) => {
        if (process.processStatusId === 2) {
            return 'Action Required';
        } else if (process.processStatusId === 3) {
            return 'Completed';
        } else if (process.processStatusId === 0) {
            return 'Awaiting Documentation';
        } else {
            return 'Processing';
        }
    };

    const getPaginatedData = () => {
        const formattedData = transformProcessesData(processes);
        return formattedData;
    };

    const handleGoBack = () => {
        if (breadcrumbs.length > 1) {
            const previousBreadcrumb = breadcrumbs[breadcrumbs.length - 2];
            let targetFolderId = -1;
            if (
                previousBreadcrumb.href &&
                previousBreadcrumb.href !== '/folder'
            ) {
                const match = previousBreadcrumb.href.match(/\/folder\/(\d+)/);
                if (match && match[1]) {
                    targetFolderId = parseInt(match[1], 10);
                }
            }

            if (targetFolderId === -1) {
                navigate('/folder');
            } else {
                navigate(`/folder/${targetFolderId}`);
            }
        } else {
            navigate('/folder');
        }
    };

    const generateTreeData = (folders: FolderResponse[]): TreeNode[] => {
        if (!folders || folders.length === 0) {
            return [];
        }

        return folders.map((folder) => ({
            title: folder.folderName,
            key: folder.folderId.toString(),
            icon:
                folder.parentFolderId === selectedFolderId
                    ? FolderIcon.SubFolder
                    : FolderIcon.RootFolderIcon,
            iconOpened: FolderIcon.SubFolder,
            iconClosed: FolderIcon.SubFolder,
            children: [],
        }));
    };

    const handleRowClick = (event: MouseEvent, index: number, data: any) => {
        navigate(`/process/${data.id}`);
    };

    const handleTabChange = (index: number) => {
        setActiveTab(index);
    };

    const getParentPersona = (
        user: any
    ): 'admin' | 'user' | 'member' | undefined => {
        switch (user.parentFolderPersonaId) {
            case 1:
                return 'admin';
            case 2:
                return 'user';
            case 3:
                return 'member';
            default:
                return undefined;
        }
    };

    const currentFolderPersonaId = users?.folderUsers.find(
        (user) => user.userId === userInfo?.id
    )?.folderPersonaId;

    return (
        <div className="my-processes">
            <div className="layout">
                <div className="sidebar">
                    <div className="search-folders">
                        <Search
                            key={`search-${selectedFolderId}`}
                            searchType="secondary"
                            placeholder="Search Folders"
                            value={inputValue}
                            onChange={setInputValue}
                        />
                        <div className="file-explorer">
                            {/* Back button (conditionally rendered) */}
                            {breadcrumbs.length > 1 && ( // Only show if you're not at the root
                                <Button
                                    kind="text"
                                    compact
                                    icon="icon-arrow-left-outline"
                                    style={{ marginBottom: '1rem' }}
                                    onClick={handleGoBack}
                                >
                                    Back
                                </Button>
                            )}

                            {isLoading && (
                                <Loading
                                    loadingType="circular"
                                    indeterminate={true}
                                    compact={false}
                                ></Loading>
                            )}
                            {!isLoading && folderData?.folders && (
                                <FolderStructure
                                    searchText={inputValue}
                                    onFolderClick={(node) =>
                                        handleSelectFolder(
                                            node.title,
                                            parseInt(node.key, 10)
                                        )
                                    }
                                    folders={generateTreeData(
                                        inputValue.trim()
                                            ? folderData.folders.filter(
                                                  (folder) =>
                                                      folder.folderName
                                                          .toLowerCase()
                                                          .includes(
                                                              inputValue.toLowerCase()
                                                          )
                                              )
                                            : folderData.folders
                                    )}
                                    key={`folder-structure-${selectedFolderId}`}
                                />
                            )}
                        </div>
                    </div>
                    {(
                        selectedFolderId === -1
                            ? !(userInfo && userInfo.roleId !== 1)
                            : !hasAccess
                    ) ? (
                        <Tooltip content="You do not have permission to create a folder here.">
                            <div style={{ display: 'inline-block' }}>
                                <Button
                                    kind="primary"
                                    icon="icon-circle-plus-outline"
                                    onClick={handleCreateFolder}
                                    className="create-folder-button"
                                    disabled
                                >
                                    {selectedFolderId === -1
                                        ? 'Create folder'
                                        : 'Create subfolder'}
                                </Button>
                            </div>
                        </Tooltip>
                    ) : (
                        <Button
                            kind="primary"
                            icon="icon-circle-plus-outline"
                            onClick={handleCreateFolder}
                            className="create-folder-button"
                        >
                            {selectedFolderId === -1
                                ? 'Create folder'
                                : 'Create subfolder'}
                        </Button>
                    )}
                </div>
                <div className="main-content">
                    <div className="table-container-home">
                        {selectedFolderId === -1 ? (
                            <div className="illustration-container">
                                <img
                                    src={Illustration4}
                                    alt="Illustration"
                                    className="select-folder-illustration"
                                />
                                <span>
                                    Please select a folder to view engagements.
                                </span>
                            </div>
                        ) : hasAccess ? (
                            <>
                                <div className="breadcrumb-container">
                                    <div className="breadcrumb-list">
                                        <Breadcrumbs
                                            items={breadcrumbs}
                                            onClick={handleBreadcrumbClick}
                                        />
                                    </div>
                                    <div className="breadcrumb-actions">
                                        <Button
                                            kind="tertiary"
                                            icon="icon-archive-outline"
                                            onClick={handleArchive}
                                            disabled={
                                                userInfo?.roleId === 1 &&
                                                currentFolderPersonaId !== 1
                                            }
                                        >
                                            Archive folder
                                        </Button>
                                        <Button
                                            onClick={() => setIsPopupOpen(true)}
                                            kind="primary"
                                            icon="icon-add-user-outline"
                                            disabled={
                                                userInfo?.roleId === 1 &&
                                                currentFolderPersonaId !== 1
                                            }
                                        >
                                            Manage access
                                        </Button>
                                    </div>
                                </div>
                                <div className="title-and-search">
                                    <div className="title-container">
                                        {isEditingFolderName ? (
                                            <>
                                                <span
                                                    className="Appkit4-icon icon-arrow-left-small-outline"
                                                    style={{
                                                        marginTop: '8px',
                                                    }}
                                                    onClick={() =>
                                                        setisEditingFolderName(
                                                            false
                                                        )
                                                    }
                                                ></span>
                                                <input
                                                    type="text"
                                                    className="editable-input"
                                                    value={folderName}
                                                    onChange={handleNameChange}
                                                    style={{
                                                        margin: '0px 20px',
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <span className="folder-title">
                                                    {folderName}
                                                </span>
                                                <span
                                                    className="Appkit4-icon icon-edit-outline"
                                                    onClick={handleEditClick}
                                                    title="Edit Folder Name"
                                                    style={{
                                                        marginTop: '10px',
                                                    }}
                                                ></span>
                                            </>
                                        )}
                                        {isEditingFolderName && (
                                            <Button
                                                className="save-btn"
                                                onClick={handleSaveClick}
                                            >
                                                Save
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="table-container-engagements">
                                    <ListingTable
                                        title=""
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        onTabChange={handleTabChange}
                                        columns={columns}
                                        data={getPaginatedData()}
                                        showSearch={true}
                                        totalCount={processes.length}
                                        currentPage={currentPage}
                                        pageSize={pageSize}
                                        onPageChange={setCurrentPage}
                                        onRowClick={handleRowClick}
                                        className="cursor-pointer-rows"
                                    />
                                </div>
                                <Button
                                    className="dropdown-button"
                                    icon="icon-circle-plus-outline"
                                    onClick={() => handleCreateProcess(1)}
                                >
                                    Create process
                                </Button>
                            </>
                        ) : (
                            <div className="illustration-container">
                                <img
                                    src={Illustration5}
                                    alt="Request Access Illustration"
                                    className="select-folder-illustration"
                                />
                                <span>
                                    You do not have access to this folder.
                                    Please request access from an admin.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <DeleteModal
                content={deleteModalContent()}
                visible={confirmationVisible}
                onClose={() => setConfirmationVisible(false)}
                title="Archive folder"
            />
            <DeleteModal
                content={createFolderModalContent()}
                visible={isCreateFolderModalOpen}
                onClose={() => setIsCreateFolderModalOpen(false)}
                title="Create folder"
            />
            <FolderConfig
                visible={isPopupOpen}
                folderId={selectedFolderId ?? 0}
                folderData={{
                    name: folderName,
                    members:
                        users?.folderUsers
                            .filter((user) => user.folderPersonaId !== 3)
                            .map((user) => ({
                                id: user.userId,
                                name: user.userName,
                                email: user.userEmail,
                                persona:
                                    user.folderPersonaId === 1
                                        ? 'admin'
                                        : 'user',
                                parentPersona: getParentPersona(user),
                                folderPersonaId: user.folderPersonaId,
                            })) || [],
                }}
                allUsers={
                    users?.folderUsers.map((user) => ({
                        id: user.userId,
                        name: user.userName,
                        email: user.userEmail,
                        persona:
                            user.folderPersonaId === 1
                                ? 'admin'
                                : user.folderPersonaId === 2
                                ? 'user'
                                : 'user', // members (folderPersonaId 3) are mapped as 'user' but with special identifier
                        parentPersona: getParentPersona(user),
                        folderPersonaId: user.folderPersonaId, // Add the original ID for identification
                    })) || []
                }
                onClose={() => setIsPopupOpen(false)}
                onSuccess={() => refetchFolderUsers()}
            />
        </div>
    );
};

export default MyProcesses;
